// Snapshot of D1 via GET /api/roadmap/finca-market-inc on 2026-04-07.
// Programs array is imported from programs.generated.ts (locally customizable).
// All other fields mirror D1 exactly — edit here for local overrides.
import type { RoadmapCompanyConfig } from '@/types/roadmap.types';
import { GENERATED_PROGRAMS } from './programs.generated';

export const fincaMarketIncConfig: RoadmapCompanyConfig = {
  id: 'finca-market-inc',

  company: {
    name: 'Finca Market Inc',
    contact_name: '',
    contact_title: '',
    logo: '',
    founded_year: 2026,
    headquarters: { city: '', state: '', country: 'United States' },
    additional_locations: [
      { city: '', state: '', country: 'Colombia', status: 'planned' },
      { city: '', state: '', country: 'Costa Rica', status: 'planned' },
    ],
    employee_count: 1,
    annual_revenue_range: 'Pre-revenue',
    entity_type: 'for-profit',
    industry: 'Software',
    industry_tags: ['agtech', 'supply chain', 'AI', 'climate', 'traceability'],
    technology_description:
      'Digital infrastructure platform for smallholder coffee supply chains, integrating marketplace, farmer training, AI advisory tools, quality and soil labs, logistics workflows, and MRV systems.',
    technology_keywords: ['coffee traceability', 'farmer advisory', 'MRV', 'soil testing', 'quality labs'],
    trl: 4,
    development_status: 'prototype',
    certifications: [],
    ownership_demographics: [],
  },

  investment_layers: [
    {
      id: 'rd',
      label: 'Applied R&D',
      description:
        'AI advisory tools, traceability architecture, MRV logic, data interoperability, and core platform systems development.',
      priority: 'high',
      details: ['AI advisory tools', 'Traceability software', 'MRV analytics', 'Data interoperability'],
    },
    {
      id: 'prototyping',
      label: 'Pilots & Prototyping',
      description:
        'Early deployments, partner integrations, field workflows, and user testing across coffee supply-chain operations.',
      priority: 'high',
      details: ['Pilot deployments', 'Workflow integration', 'User testing', 'Field validation'],
    },
    {
      id: 'deployment',
      label: 'Implementation',
      description:
        'Rollout of platform services with buyers, producers, labs, and supply-chain partners across U.S. and Latin America operations.',
      priority: 'medium',
      details: ['Buyer onboarding', 'Farmer onboarding', 'Implementation support', 'Regional rollout'],
    },
    {
      id: 'scaling',
      label: 'Scaling & Market Expansion',
      description:
        'Growth-stage commercialization, export market development, partnership expansion, and entry into priority coffee-producing regions.',
      priority: 'medium',
      details: ['Export expansion', 'Sales enablement', 'Partnership growth', 'Market entry'],
    },
  ],

  geography: {
    primary_geography: 'United States',
    secondary_geographies: ['Colombia', 'Costa Rica', 'Latin America'],
    state: '',
    city: '',
    eu_presence: { planned: false, country: '', region: '' },
    eligible_jurisdictions: ['US-federal', 'state', 'USDA', 'SBIR', 'STTR'],
  },

  eligibility_checklist: {
    jurisdiction: 'United States',
    items: [
      'U.S.-based for-profit small business',
      'SAM.gov registration and UEI required for federal awards',
      'Majority U.S. ownership/control for SBIR/STTR',
      'Able to show commercialization pathway',
      'Can participate with partners for consortium or university-led grants',
    ],
    groups: [
      {
        label: 'Federal SBIR/STTR readiness',
        items: [
          'Active SAM.gov registration',
          'UEI/CAGE active',
          'Small business size compliance',
          'Commercialization plan',
          'Technical project narrative',
        ],
        unlock_note: 'Required for NSF and USDA SBIR/STTR submissions.',
      },
      {
        label: 'Partner-led opportunities',
        items: [
          'University or research partner',
          'Producer organization or NGO partner',
          'Lab or validation partner',
          'Letters of support/MOU',
        ],
        unlock_note: 'Unlocks USDA climate-smart, NSF consortium, and workforce/training programs.',
      },
      {
        label: 'Export and state programs',
        items: [
          'State export office contact',
          'International market plan',
          'Buyer development targets',
          'Local EDO or SBDC support',
        ],
        unlock_note: 'Unlocks STEP and state innovation/export programs.',
      },
    ],
  },

  programs: [...GENERATED_PROGRAMS],

  timeline: [],

  primary_count: 3,

  proposal: {
    consultant: {
      name: 'Scout AI',
      title: 'Grant Strategist',
      company: 'Allegory Studios',
      headshot: '',
      email: '',
    },
    engagement_scope: 'Funding roadmap and proposal support',
    time_horizon: '12 months',
    created_at: '2026-04-10',
    version: 2,
  },

  clientBrand: {
    logo: '',
    logoDark: '',
    icon: '',
    companyName: 'Finca Market Inc',
    colors: {
      primary: '#5B3A29',
      secondary: '#1E6F5C',
      accent: '#D4A373',
    },
  },

  whiteLabel: false,

  intro_statement:
    'Funding roadmap organized by investment layer for a digital infrastructure platform supporting traceable, climate-smart coffee supply chains across applied R&D, pilots, validation, implementation, and market expansion.',
};
